import { useCallback, useMemo, useRef, useState } from 'react';

/**
 * Undo and redo for the editor.
 *
 * Deliberately generic: it keeps whole before/after values rather than describing how to
 * reverse each kind of edit. Moves, rotations, property changes, additions and deletions
 * are then all the same operation to it, which is both far less code than per-mutation
 * inverse logic and impossible to get subtly wrong for one element type only.
 *
 * Held as one list with a cursor rather than two stacks. Undo and redo are the same
 * movement either way, but a list is also the thing the history panel shows: jumping seven
 * steps back is moving the cursor, not replaying seven undos and hoping they compose.
 */

/** Names the edit that produced a state, for the history panel. An i18n key, never a sentence. */
export interface HistoryEntry {
  readonly labelKey: string;
  /** Passed to `t` alongside the key, for "Deleted 4 features". */
  readonly count?: number;
}

export interface CommitOptions extends HistoryEntry {
  /**
   * Merges this change into the previous entry instead of stacking a new one, so a burst of
   * small edits — holding an arrow key, dragging a desk across the plan — undoes as the
   * single gesture it felt like rather than one step per frame.
   */
  readonly coalesceKey?: string;
}

export interface UndoableState<T> {
  readonly current: T;
  readonly canUndo: boolean;
  readonly canRedo: boolean;
  readonly commit: (next: T, options?: CommitOptions) => void;
  /** Replaces the value without touching the history, for loading a floorplan. */
  readonly reset: (next: T) => void;
  readonly undo: () => void;
  readonly redo: () => void;
  /** Ends the current coalescing run, so the next commit starts a fresh history entry. */
  readonly endCoalescing: () => void;
  /** Every state held, oldest first. Index 0 is the plan as it was opened. */
  readonly entries: readonly HistoryEntry[];
  /** Which of [entries] is showing. */
  readonly index: number;
  /** Moves straight to a step. Out-of-range indexes are ignored rather than clamped. */
  readonly jumpTo: (index: number) => void;
}

interface Snapshot<T> extends HistoryEntry {
  readonly value: T;
}

interface History<T> {
  readonly snapshots: readonly Snapshot<T>[];
  readonly index: number;
}

const HISTORY_LIMIT = 100;

/** What the first entry is called: not an edit, but the state every undo leads back to. */
export const OPENED_LABEL_KEY = 'floorplan-history-opened';

export const useUndoRedo = <T,>(initial: T): UndoableState<T> => {
  const [history, setHistory] = useState<History<T>>({
    snapshots: [{ value: initial, labelKey: OPENED_LABEL_KEY }],
    index: 0,
  });
  const coalesceKey = useRef<string | undefined>(undefined);

  const commit = useCallback((next: T, options?: CommitOptions) => {
    setHistory((current) => {
      const continuingRun = options?.coalesceKey !== undefined
        && options.coalesceKey === coalesceKey.current;
      coalesceKey.current = options?.coalesceKey;

      // Anything after the cursor is a future that this edit has just replaced.
      const kept = current.snapshots.slice(0, current.index + 1);

      // Continuing a run replaces the state in place, so the entry already on the stack
      // still points at the state from before the whole gesture began.
      if (continuingRun) {
        const merged = [...kept];
        merged[merged.length - 1] = {
          value: next,
          labelKey: options?.labelKey ?? merged[merged.length - 1].labelKey,
          count: options?.count,
        };

        return { snapshots: merged, index: merged.length - 1 };
      }

      const grown = [
        ...kept,
        { value: next, labelKey: options?.labelKey ?? 'floorplan-history-edited', count: options?.count },
      ];

      // Trimming from the front moves every index down with it, the cursor included —
      // otherwise the hundred-and-first edit silently jumps the panel a step back.
      const trimmed = grown.slice(-HISTORY_LIMIT);

      return { snapshots: trimmed, index: trimmed.length - 1 };
    });
  }, []);

  const reset = useCallback((next: T) => {
    coalesceKey.current = undefined;
    setHistory({ snapshots: [{ value: next, labelKey: OPENED_LABEL_KEY }], index: 0 });
  }, []);

  const step = useCallback((delta: number) => {
    coalesceKey.current = undefined;

    setHistory((current) => {
      const next = current.index + delta;
      return next < 0 || next >= current.snapshots.length ? current : { ...current, index: next };
    });
  }, []);

  const undo = useCallback(() => step(-1), [step]);

  const redo = useCallback(() => step(1), [step]);

  const jumpTo = useCallback((index: number) => {
    coalesceKey.current = undefined;

    setHistory((current) => (
      index < 0 || index >= current.snapshots.length ? current : { ...current, index }
    ));
  }, []);

  const endCoalescing = useCallback(() => {
    coalesceKey.current = undefined;
  }, []);

  const entries = useMemo(
    // Stripped of the values: the panel renders labels, and handing it whole floorplans
    // would make every row a reason to re-render on an edit it does not show.
    () => history.snapshots.map(({ labelKey, count }) => ({ labelKey, count })),
    [history.snapshots],
  );

  return {
    current: history.snapshots[history.index].value,
    canUndo: history.index > 0,
    canRedo: history.index < history.snapshots.length - 1,
    commit,
    reset,
    undo,
    redo,
    endCoalescing,
    entries,
    index: history.index,
    jumpTo,
  };
};

export default useUndoRedo;
